import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { neverLoaded, usePolling } from "../lib/use-polling";
import { api } from "../lib/api";
import { PageHelp } from "../components/PageHelp";
import { PageHeader } from "../components/primitives/PageHeader";
import { Segmented } from "../components/primitives/Segmented";
import { Modal } from "../components/primitives/Modal";
import { Table, type Column } from "../components/primitives/Table";
import { Badge } from "../components/primitives/Badge";
import { Button } from "../components/primitives/Button";
import { Pulse } from "../components/primitives/Pulse";
import { EmptyState } from "../components/primitives/EmptyState";
import { InfoTip } from "../components/primitives/InfoTip";
import { FormField, TextInput } from "../components/primitives/FormField";
import { buildClientRows, filterClientRows, SLUG_RE, UUID_RE, type ClientRow } from "./client-rows";

// Clients (spec §6.1): the CS home page. One row per client; the workspace is
// one click away. Creating a client requires the Jump organization id because
// events from Jump carry it — an auto-generated id would never receive events.

export interface ClientOrg { id: string; name: string }
export interface ConnectorInstance {
  id: string;
  organizationId: string;
  connectorType: string;
  environment?: string | null;
  config?: Record<string, unknown>;
}
export interface ClientEnvironment { name: string; organizationId: string; disabled?: boolean }

// ─── ClientsTable — pure presentable ─────────────────────────────────────────

/** The clients table: one row per client with its environments, HubSpot state
 *  and pipeline counts. Presentational — the page owns the data and filters. */
export function ClientsTable({ rows, onOpen }: { rows: ClientRow[]; onOpen?: (id: string) => void }) {
  const columns: Column<ClientRow>[] = [
    {
      key: "client",
      label: "CLIENT",
      render: (r) => (
        <div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 500 }}>{r.name}</div>
          <div className="mono clients__id">{r.id.slice(0, 8)}</div>
        </div>
      ),
    },
    {
      key: "envs",
      label: "ENVIRONMENTS",
      render: (r) =>
        r.environments.length === 0 ? (
          <span className="page-help" style={{ margin: 0 }}>No environments</span>
        ) : (
          <span className="mono">{r.environments.join(" · ")}</span>
        ),
    },
    {
      key: "hubspot",
      label: "HUBSPOT",
      render: (r) => (
        <span className="client-hubspot">
          {r.hubspot.map((h) => (
            <span key={h.env ?? "any"} className="client-hubspot__env">
              {h.env ? <span className="mono client-hubspot__name">{h.env}</span> : null}
              <Badge tone={h.connected ? "ok" : "muted"}>{h.connected ? "Connected" : "Not connected"}</Badge>
            </span>
          ))}
        </span>
      ),
    },
    {
      key: "pipelines",
      label: "PIPELINES",
      render: (r) =>
        r.active + r.paused === 0 ? (
          <span className="page-help" style={{ margin: 0 }}>None</span>
        ) : (
          <span>
            {r.active} active{r.paused ? ` · ${r.paused} paused` : ""}
          </span>
        ),
    },
    {
      key: "open",
      label: "",
      width: "80px",
      render: (r) => (
        <Button
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onOpen?.(r.id);
          }}
        >
          Open
        </Button>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      rows={rows}
      rowKey={(r) => r.id}
      onRowClick={onOpen ? (r) => onOpen(r.id) : undefined}
      empty={<EmptyState title="No clients yet." />}
    />
  );
}

// ─── NewClientModal ───────────────────────────────────────────────────────────

/**
 * Create a client. The name and the Jump organization id are both validated
 * here before anything is sent, so a typo comes back as a sentence under the
 * field rather than as a server error after the fact.
 *
 * Not dismissible while the request is in flight: the client would still be
 * created, and the operator would have been shown a cancellation.
 */
export function NewClientModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState("");
  const [id, setId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** The first thing wrong with the form, in plain words, or null when it is ready. */
  function validate(): string | null {
    if (!name.trim()) return "Name is required.";
    if (!SLUG_RE.test(name.trim())) return "Name must be lowercase letters, digits and hyphens, 3 to 63 characters.";
    if (!id.trim()) return "Jump organization id is required.";
    if (!UUID_RE.test(id.trim())) return "Jump organization id must be a UUID, like 3b241101-e2bb-4255-8caf-4136c566a962.";
    return null;
  }

  /** Create the client and hand its id back to the page, which navigates into it. */
  async function create() {
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    setBusy(true);
    try {
      const { organization } = await api.createOrganization({ id: id.trim(), name: name.trim() });
      onCreated(organization?.id ?? id.trim());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="New client"
      onClose={onClose}
      dismissible={!busy}
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void create()} disabled={busy}>
            {busy ? "Creating…" : "Create client"}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => { e.preventDefault(); void create(); }}
        noValidate
      >
        <FormField label="Name" hint="Lowercase, hyphenated. This is how the client appears across Anansi.">
          <TextInput aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="rip-city" />
        </FormField>
        <FormField
          label="Jump organization id"
          hint="Paste the organization id from Jump. Events from Jump carry this id and Anansi uses it to route them to this client."
        >
          <TextInput
            aria-label="Jump organization id"
            value={id}
            onChange={(e) => setId(e.target.value)}
            placeholder="3b241101-e2bb-4255-8caf-4136c566a962"
          />
        </FormField>
        {error ? <div className="field__error" role="alert">{error}</div> : null}
      </form>
    </Modal>
  );
}

// ─── Clients page ─────────────────────────────────────────────────────────────

/** CS home (spec §6.1): every client, filterable by name and environment. */
export function Clients() {
  const nav = useNavigate();
  const orgsQ = usePolling(() => api.listOrganizations(), 60000);
  const instancesQ = usePolling(() => api.listConnectorInstances(), 30000);
  const environmentsQ = usePolling(() => api.listEnvironments(), 60000);
  const pipelinesQ = usePolling(() => api.listPipelines(), 60000);
  const [nameFilter, setNameFilter] = useState("");
  const [envFilter, setEnvFilter] = useState("");
  const [showNew, setShowNew] = useState(false);

  const orgs: ClientOrg[] = orgsQ.data?.organizations ?? [];
  const instances: ConnectorInstance[] = instancesQ.data?.instances ?? [];
  // Environments and pipelines are soft dependencies: while either has no
  // data yet, or after it fails, the list still renders with what it has (no
  // environments → a single any-env HubSpot entry; no pipelines → "None").
  const environments: ClientEnvironment[] = environmentsQ.data?.environments ?? [];
  const pipelines = pipelinesQ.data?.pipelines ?? [];

  const rows = useMemo(() => buildClientRows(orgs, instances, environments, pipelines), [orgs, instances, environments, pipelines]);
  const envOptions = useMemo(() => {
    const names = Array.from(new Set(rows.flatMap((r) => r.environments))).sort();
    return [{ value: "", label: "All" }, ...names.map((n) => ({ value: n, label: n }))];
  }, [rows]);
  const visible = useMemo(() => filterClientRows(rows, { name: nameFilter, env: envFilter }), [rows, nameFilter, envFilter]);

  if (orgsQ.status === "loading" || instancesQ.status === "loading" || environmentsQ.status === "loading" || pipelinesQ.status === "loading") {
    return <Pulse />;
  }
  if (neverLoaded(orgsQ) || neverLoaded(instancesQ)) {
    return <p className="page-help">Couldn't load clients. Retrying.</p>;
  }

  return (
    <div>
      <PageHeader
        eyebrow={
          <>
            Anansi
            <InfoTip termKey="client" />
          </>
        }
        title="Clients"
        summary={`${rows.length} ${rows.length === 1 ? "client" : "clients"}`}
        actions={<Button variant="primary" onClick={() => setShowNew(true)}>New client</Button>}
      />
      <PageHelp />

      <div className="clients-toolbar">
        <TextInput
          aria-label="Filter by name"
          placeholder="Filter by name"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
        />
        {envOptions.length > 1 ? (
          <Segmented options={envOptions} value={envFilter} onChange={setEnvFilter} label="Environment" />
        ) : null}
      </div>

      <ClientsTable rows={visible} onOpen={(id) => nav(`/clients/${id}`)} />

      {showNew ? (
        <NewClientModal
          onClose={() => setShowNew(false)}
          onCreated={(id) => {
            setShowNew(false);
            nav(`/clients/${id}`);
          }}
        />
      ) : null}
    </div>
  );
}
